/**
 * Local draft persistence: typed work survives a refresh, an accidental
 * navigation or a crash.
 *
 * Everything here is best effort and silent on failure. `localStorage` throws
 * on quota and in some private-browsing modes, and a stored draft may have been
 * written by an older build of a form — neither is worth costing the user a
 * screen, so both end in "no draft" rather than an error.
 *
 * Nothing from an auth form is ever stored. Onboarding and knowledge-base
 * forms only.
 */

/**
 * Bumped when a stored payload's meaning changes. Drafts written under an older
 * version are discarded on read instead of being fed to a form that no longer
 * understands them.
 */
const DRAFT_VERSION = 1;

const DRAFT_PREFIX = 'outpost:draft';

/**
 * Record forms that hold enough typed work to be worth saving. Mostly a subset
 * of `KnowledgeBaseSection`, redeclared here so `lib/` does not reach into a
 * feature. Assets are absent on purpose: that form edits an already-uploaded
 * file's kind and tags, and a `File` cannot be persisted at all.
 *
 * `planned-post` is the content calendar's, not the knowledge base's. It shares
 * the key shape because it is the same kind of thing — one business-scoped
 * record form — and a second namespace would buy nothing.
 */
export type DraftSection =
  | 'brand-identity'
  | 'voice'
  | 'products'
  | 'proof-points'
  | 'inspiration'
  | 'calendar'
  | 'planned-post';

export interface DraftSnapshot<T> {
  /** ISO timestamp of the write that produced this draft. */
  savedAt: string;
  data: T;
}

export interface DraftRestore<T> {
  /** The draft's data when a usable draft existed, otherwise `pristine`. */
  value: T;
  /** When a draft was applied, the time it was written. Null when none was. */
  savedAt: string | null;
}

/**
 * Onboarding answers for one user. Scoped by user id so two accounts on the
 * same machine never see each other's setup.
 */
export function onboardingDraftKey(userId: string): string {
  return `${DRAFT_PREFIX}:onboarding:${userId}`;
}

/**
 * The add-a-brand form for one user. Its own key, not the onboarding one, so
 * an abandoned "one more brand" never reopens on a first-run screen.
 */
export function newBrandDraftKey(userId: string): string {
  return `${DRAFT_PREFIX}:new-brand:${userId}`;
}

/**
 * One knowledge-base form. `itemId` is the row being edited, `null` for a
 * create form, and `'singleton'` for brand identity — the one record per
 * business that has no id of its own until it is first saved.
 */
export function knowledgeBaseDraftKey(
  section: DraftSection,
  businessId: string,
  itemId: string | null,
): string {
  return `${DRAFT_PREFIX}:kb:${section}:${businessId}:${itemId ?? 'new'}`;
}

/** The brand-identity singleton's key. */
export function brandIdentityDraftKey(businessId: string): string {
  return knowledgeBaseDraftKey('brand-identity', businessId, 'singleton');
}

/**
 * The generation brief for one business — platform, vibe and topic only.
 *
 * The chosen image is deliberately not part of it: an asset selection is a
 * pointer to a server row, and re-applying a stale one after a refresh would be
 * a worse guess than asking again.
 */
export function generationDraftKey(businessId: string): string {
  return `${DRAFT_PREFIX}:generation:${businessId}`;
}

/**
 * One slide's unsaved layer edits, scoped to the slide rather than the request:
 * the editor edits one slide at a time and is keyed on its id.
 *
 * The payload carries the slide's `updated_at` as a baseline, because unlike a
 * knowledge-base form this draft describes a *version* of a server row that is
 * re-rendered under it. A draft written against a slide the server has since
 * moved on from is dropped rather than replayed.
 */
export function slideDraftKey(slideId: string): string {
  return `${DRAFT_PREFIX}:slide:${slideId}`;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Same broad kind of value — array, object, or the same primitive type. */
function sameKind(candidate: unknown, reference: unknown): boolean {
  if (reference === null) return candidate === null;
  if (Array.isArray(reference)) return Array.isArray(candidate);
  if (isPlainObject(reference)) return isPlainObject(candidate);
  return typeof candidate === typeof reference;
}

/**
 * Structural check, one level deep: the candidate must carry exactly the
 * reference's field names, each holding the same kind of value. That is enough
 * to reject a draft written by an earlier shape of the form; it is not a
 * validator, so forms still narrow anything they hand to a `<select>`.
 */
function matchesShape(candidate: unknown, reference: unknown): boolean {
  if (!isPlainObject(reference) || !isPlainObject(candidate)) return false;
  const referenceKeys = Object.keys(reference);
  if (referenceKeys.length !== Object.keys(candidate).length) return false;
  return referenceKeys.every(
    (key) => Object.hasOwn(candidate, key) && sameKind(candidate[key], reference[key]),
  );
}

/** JSON for a form value, or null when it cannot be serialised (cycles). */
export function serializeDraft(value: unknown): string | null {
  try {
    return JSON.stringify(value) ?? null;
  } catch {
    return null;
  }
}

/**
 * Reads and validates one draft. `isMatch` guards the payload shape; a stored
 * value that fails the envelope or the guard is dropped from storage, not
 * returned.
 */
export function readDraft<T>(
  key: string | null,
  isMatch: (data: unknown) => data is T,
): DraftSnapshot<T> | null {
  if (key === null) return null;

  let raw: string | null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (raw === null) return null;

  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      isPlainObject(parsed) &&
      parsed.v === DRAFT_VERSION &&
      typeof parsed.saved_at === 'string'
    ) {
      if (isMatch(parsed.data)) return { savedAt: parsed.saved_at, data: parsed.data };
    }
  } catch {
    // Truncated or hand-edited JSON. Falls through to the sweep below.
  }

  clearDraft(key);
  return null;
}

/**
 * The value a form should open with, plus the time any recovered draft was
 * written. `pristine` is the form with no draft applied — the empty form, or
 * the record as the server has it — and doubles as the shape reference.
 *
 * Call this from a lazy `useState` initialiser: the first render already holds
 * the user's work, so no effect ever writes state synchronously.
 */
export function restoreDraft<T>(key: string | null, pristine: T): DraftRestore<T> {
  const snapshot = readDraft<T>(key, (data): data is T => matchesShape(data, pristine));
  if (snapshot === null) return { value: pristine, savedAt: null };

  // A draft identical to the saved record has nothing to recover, and showing
  // a recovery notice for it would be a lie. Clear it instead.
  if (serializeDraft(snapshot.data) === serializeDraft(pristine)) {
    clearDraft(key);
    return { value: pristine, savedAt: null };
  }

  return { value: snapshot.data, savedAt: snapshot.savedAt };
}

/**
 * Writes the versioned envelope around an already-serialised payload.
 *
 * `saved_at` is an ISO timestamp — digits, dashes, colons and a `Z`, nothing
 * that needs escaping — so the envelope is composed around the payload rather
 * than re-serialising a value the caller has already stringified.
 */
export function writeDraftJson(key: string, dataJson: string): void {
  try {
    window.localStorage.setItem(
      key,
      `{"v":${DRAFT_VERSION},"saved_at":"${new Date().toISOString()}","data":${dataJson}}`,
    );
  } catch {
    // Quota exceeded, storage disabled, private mode. A draft is a convenience.
  }
}

export function clearDraft(key: string | null): void {
  if (key === null) return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Same story: nothing the user can act on.
  }
}

/**
 * Removes every draft whose key starts with `prefix`. Used once, to sweep one
 * user's onboarding keys when setup is filed — including any left by an earlier
 * key shape. This is not a general collector; drafts are cleared by the form
 * that owns them.
 */
export function clearDraftsByPrefix(prefix: string): void {
  // Storage also holds the Supabase session. Nothing outside the draft
  // namespace is ever sweepable, whatever the caller passes.
  if (!prefix.startsWith(`${DRAFT_PREFIX}:`)) return;

  try {
    const doomed: string[] = [];
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (key !== null && key.startsWith(prefix)) doomed.push(key);
    }
    for (const key of doomed) window.localStorage.removeItem(key);
  } catch {
    // Storage unavailable: there is nothing to sweep.
  }
}
